import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FamilyIcon, WalletIcon, FeesIcon, CalendarIcon, TransportIcon,
  CheckIcon, GrowthIcon, ArrowRightIcon, LocationIcon, ClockIcon,
  BrandMarkIcon, ShieldIcon, BusRouteIcon, BookIcon, MealIcon,
} from "./icons";
import {
  Users, School as SchoolIcon, Heart, Phone, UserCheck, Link2,
  Sun, Moon, Coffee, Bus, BookOpen, Utensils, FlaskConical, Trophy,
  Home, TrendingUp, Sparkles, ChevronRight, Activity,
  CheckCircle2, Circle, Clock3, Award, Star, Zap, Droplets,
  Wind, Thermometer, CloudRain, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { C, ACCENT_GRADIENT, BRAND_GRADIENT, SHADOWS, type Student, type FeeTerm, type Checkpoint, type Visit, type NavSection, type ChildTab, childColor } from "./tokens";
import { PremiumCard, SectionHeader, MiniBar, Pill, Avatar, ProgressRing } from "./primitives";
import { fmtUGX, shortDateTime } from "@/lib/format";

const EASE = [0.4, 0, 0.2, 1] as const;

// ── Disciplined palette: forest brand + 2 semantic accents only ──
const INK = "#1C1917";      // primary brand ink
const INK_SOFT = "#3C3530"; // secondary text
const MUTED = "#8B6F47";    // muted text
const GOOD = "#8B6F47";     // success/positive
const WARN = "#8B6F47";     // warning/attention (darker for contrast)
const DANGER = "#C53B30";   // urgent/overdue

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return { text: "Good morning", icon: Sun, phase: "Morning", gradient: "linear-gradient(135deg, #FFF4E5 0%, #FFF8F0 60%, #FFFDF5 100%)", glow: C.orange };
  if (h < 17) return { text: "Good afternoon", icon: Coffee, phase: "Afternoon", gradient: "linear-gradient(135deg, #E3F9E5 0%, #F0FFF4 60%, #F8FFF9 100%)", glow: C.green };
  return { text: "Good evening", icon: Moon, phase: "Evening", gradient: "linear-gradient(135deg, #F5E6FD 0%, #FAF0FF 60%, #FDFAFF 100%)", glow: C.purple };
}

type TimelineEvent = {
  time: string; title: string; subtitle?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  status: "done" | "active" | "upcoming"; color: string;
};

function getTimelineForChild(child: Student): TimelineEvent[] {
  const hour = new Date().getHours();
  return [
    { time: "07:08", title: "Boarded Bus", subtitle: child.bus_route ?? "Route B", icon: Bus, status: hour > 7 ? "done" : "upcoming", color: INK },
    { time: "07:43", title: "Arrived Safely", subtitle: child.school_name ?? "School", icon: CheckCircle2, status: hour > 7 ? "done" : "upcoming", color: GOOD },
    { time: "08:02", title: "Attendance Confirmed", icon: CheckCircle2, status: hour > 8 ? "done" : "upcoming", color: GOOD },
    { time: "10:22", title: "Borrowed Science Book", subtitle: "Library", icon: BookOpen, status: hour > 10 ? "done" : "upcoming", color: INK },
    { time: "12:19", title: "Purchased Lunch", subtitle: "Meal Plan A", icon: Utensils, status: hour > 12 ? "done" : hour === 12 ? "active" : "upcoming", color: INK },
    { time: "14:05", title: "Science Practical", subtitle: "Laboratory 2", icon: FlaskConical, status: hour > 14 ? "done" : hour === 14 ? "active" : "upcoming", color: INK },
    { time: "15:45", title: "Football Practice", subtitle: "Sports Field", icon: Trophy, status: hour > 15 ? "done" : hour === 15 ? "active" : "upcoming", color: GOOD },
    { time: "17:05", title: "Bus Arriving Home", subtitle: "ETA 18 mins", icon: Bus, status: hour > 17 ? "done" : "upcoming", color: INK },
  ];
}

function LivingChildCard({ student, fees, checkpoints, goChild, index }: {
  student: Student; fees: FeeTerm[]; checkpoints: Checkpoint[];
  goChild: (id: string, tab?: ChildTab) => void; index: number;
}) {
  const color = childColor([student], student.id);
  const [expanded, setExpanded] = useState(false);
  const timeline = useMemo(() => getTimelineForChild(student), [student]);
  const childFees = fees.filter(f => f.student_id === student.id);
  const childTotal = childFees.reduce((a, f) => a + f.total_ugx, 0);
  const childPaid = childFees.reduce((a, f) => a + f.paid_ugx, 0);
  const feePct = childTotal > 0 ? (childPaid / childTotal) * 100 : 100;
  const completedSteps = timeline.filter(e => e.status === "done").length;
  const activeEvent = timeline.find(e => e.status === "active");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5, ease: EASE }}
      whileHover={{ y: -4 }}
      onClick={() => goChild(student.id)}
      className="relative overflow-hidden rounded-[16px] border bg-white cursor-pointer transition-all"
      style={{ borderColor: "#E2D6C0", boxShadow: SHADOWS.premium }}
    >
      <div className="h-px" style={{ background: "#E2D6C0" }} />

      <div className="relative p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-5">
          <div className="relative shrink-0">
            <Avatar name={student.full_name} color={color} size={64} />
            <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-white flex items-center justify-center" style={{ background: student.status_dot === "green" ? GOOD : WARN }}>
              <CheckIcon className="h-2.5 w-2.5 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            {/* Name — no truncation, wraps gracefully */}
            <h3 className="text-lg font-bold tracking-tight leading-tight" style={{ color: INK }}>
              {student.full_name}
            </h3>
            <p className="text-sm mt-0.5 leading-snug" style={{ color: MUTED }}>
              {student.class_label}
            </p>
            <p className="text-xs mt-0.5 leading-snug" style={{ color: MUTED }}>
              {student.school_name ?? "School not set"}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Pill color={GOOD}><CheckIcon className="h-3 w-3" />{student.status_label ?? "In Class"}</Pill>
              {student.bus_route && <Pill color={INK}><TransportIcon className="h-3 w-3" />{student.bus_route.split(" - ")[0]}</Pill>}
            </div>
          </div>
          <ProgressRing value={(completedSteps / timeline.length) * 100} size={52} stroke={5} color={INK} />
        </div>

        {/* Current activity */}
        <div className="rounded-[16px] p-4 mb-4 border" style={{ background: `${INK}05`, borderColor: `${INK}12` }}>
          <div className="flex items-center gap-3">
            <div className="relative h-11 w-11 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: `${INK}10` }}>
              <Activity className="h-5 w-5" style={{ color: INK }} />
              {activeEvent && <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full animate-pulse border-2 border-white" style={{ background: INK }} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: MUTED }}>Currently</p>
              <p className="text-sm font-semibold tracking-tight truncate" style={{ color: INK }}>
                {activeEvent?.title ?? "Mathematics"}
              </p>
              {activeEvent?.subtitle && <p className="text-xs truncate" style={{ color: MUTED }}>{activeEvent.subtitle}</p>}
            </div>
            <div className="text-right shrink-0">
              <p className="text-2xl font-bold tracking-tight" style={{ color: INK }}>{completedSteps}</p>
              <p className="text-[9px] font-semibold uppercase tracking-tight" style={{ color: MUTED }}>of {timeline.length}</p>
            </div>
          </div>
        </div>

        {/* Today's journey — segmented progress */}
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-tight mb-3" style={{ color: MUTED }}>Today's Journey</p>
          <div className="flex items-center gap-1">
            {timeline.map((ev, i) => (
              <React.Fragment key={i}>
                <motion.div
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: i * 0.06 }}
                  className="flex-1 h-2 rounded-full origin-left"
                  style={{ background: ev.status === "done" ? GOOD : ev.status === "active" ? INK : "#E2D6C0" }}
                />
                {i < timeline.length - 1 && <div className="w-0.5" />}
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] font-medium" style={{ color: MUTED }}>{completedSteps} of {timeline.length} done</span>
            <button
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              className="text-[10px] font-semibold flex items-center gap-0.5" style={{ color: INK }}
            >
              {expanded ? "Hide" : "Details"} <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
            </button>
          </div>
        </div>

        {/* Expanded timeline */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="pt-3 border-t space-y-2.5" style={{ borderColor: "#EDE4D4" }}>
                {timeline.map((ev, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[10px] font-mono w-10 shrink-0" style={{ color: MUTED }}>{ev.time}</span>
                    {ev.status === "done" ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: GOOD }} />
                    ) : ev.status === "active" ? (
                      <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1.5 }} className="w-4 h-4 rounded-full shrink-0" style={{ background: INK }} />
                    ) : (
                      <Circle className="h-4 w-4 shrink-0" style={{ color: "#E2D6C0" }} />
                    )}
                    <span className="text-xs font-medium flex-1 truncate" style={{ color: ev.status === "upcoming" ? MUTED : INK }}>{ev.title}</span>
                    {ev.subtitle && <span className="text-[10px] truncate max-w-[100px]" style={{ color: MUTED }}>{ev.subtitle}</span>}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats grid — 2 columns to prevent clipping, larger text */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t" style={{ borderColor: "#EDE4D4" }}>
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${GOOD}12` }}>
              <WalletIcon className="h-4 w-4" style={{ color: GOOD }} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: MUTED }}>Wallet</p>
              <p className="text-sm font-bold truncate" style={{ color: INK }}>{fmtUGX(student.balance_ugx)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: feePct >= 100 ? `${GOOD}12` : `${WARN}12` }}>
              <FeesIcon className="h-4 w-4" style={{ color: feePct >= 100 ? GOOD : WARN }} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: MUTED }}>Fees</p>
              <p className="text-sm font-bold" style={{ color: feePct >= 100 ? GOOD : WARN }}>{Math.round(feePct)}% paid</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function FamilyView({ students, fees, checkpoints, visits, goChild, firstName }: {
  students: Student[]; fees: FeeTerm[]; checkpoints: Checkpoint[]; visits: Visit[];
  goChild: (id: string, tab?: ChildTab) => void;
  firstName?: string;
}) {
  const greeting = useMemo(() => getGreeting(), []);
  const GreetingIcon = greeting.icon;

  const totalWallets = students.reduce((s, c) => s + c.balance_ugx, 0);
  const totalPaid = fees.reduce((s, f) => s + f.paid_ugx, 0);
  const totalDue = fees.reduce((s, f) => s + Math.max(0, f.total_ugx - f.paid_ugx), 0);
  const totalFees = fees.reduce((s, f) => s + f.total_ugx, 0);
  const feeProgress = totalFees > 0 ? (totalPaid / totalFees) * 100 : 0;
  const schools = new Set(students.map(s => s.school_name));
  const parentFirstName = firstName ?? "Parent";

  // Unified wellbeing checks — all use INK/GOOD/WARN only
  const wellbeingChecks = [
    { label: "Attendance", value: "98%", status: "good" as const, icon: CheckCircle2, color: GOOD, pct: 98 },
    { label: "Payments", value: totalDue > 0 ? "Due Soon" : "On Track", status: totalDue > 0 ? "warning" as const : "good" as const, icon: WalletIcon, color: totalDue > 0 ? WARN : GOOD, pct: totalDue > 0 ? 60 : 100 },
    { label: "Transport", value: "Running", status: "good" as const, icon: Bus, color: INK, pct: 100 },
    { label: "Health", value: "No Alerts", status: "good" as const, icon: Heart, color: GOOD, pct: 100 },
    { label: "Messages", value: "2 New", status: "info" as const, icon: BrandMarkIcon, color: INK, pct: 75 },
  ];

  // Insights — all use INK accent, no rainbow
  const insights = [
    { icon: TrendingUp, text: `${students[0]?.full_name.split(" ")[0] ?? "Your child"}'s attendance is strong this term.`, color: GOOD, action: "View report" },
    { icon: BookIcon, text: "Reading activity increased this week.", color: INK, action: "See reading log" },
    { icon: GrowthIcon, text: "Science grades are trending upward.", color: GOOD, action: "View grades" },
    { icon: BusRouteIcon, text: "Bus route updates will appear here.", color: INK, action: "Track bus" },
    { icon: WalletIcon, text: "Set up auto-pay to never miss a due date.", color: INK, action: "Set up" },
    { icon: Award, text: "Achievement highlights will show here.", color: GOOD, action: "View" },
  ];

  const memories = students[0] ? [
    { year: "2026", title: `Linked to ${students[0].school_name ?? "school"}`, icon: SchoolIcon, color: INK },
    { year: "2026", title: "School fees plan active", icon: WalletIcon, color: GOOD },
    { year: "2026", title: "Transport confirmed", icon: Bus, color: INK },
  ] : [];

  return (
    <div className="space-y-5 sm:space-y-6 max-w-6xl mx-auto">
      {/* ═════════ COMPACT HOUSEHOLD SUMMARY ═════════ */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="relative overflow-hidden rounded-[16px] bg-white"
        style={{ border: "1px solid #E2D6C0", boxShadow: SHADOWS.premium }}
      >
        <div className="h-px" style={{ background: "#E2D6C0" }} />
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, rgba(11,42,31,0.08), rgba(0,137,123,0.08))" }}>
                <Users className="h-5 w-5" style={{ color: INK }} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ color: INK, fontFamily: '"Playfair Display", Georgia, serif' }}>
                  Your Household
                </h1>
                <p className="text-xs mt-0.5" style={{ color: MUTED }}>
                  {students.length} {students.length === 1 ? "child" : "children"} · {schools.size} {schools.size === 1 ? "school" : "schools"} · Fees {Math.round(feeProgress)}% complete
                </p>
              </div>
            </div>

            {/* Inline stats — unified palette */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-2xl px-3.5 py-2.5" style={{ background: `${GOOD}08`, border: `1px solid ${GOOD}18` }}>
                <WalletIcon className="h-4 w-4" style={{ color: GOOD }} />
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>Wallets</p>
                  <p className="text-sm font-bold" style={{ color: INK }}>{fmtUGX(totalWallets)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-2xl px-3.5 py-2.5" style={{ background: totalDue > 0 ? `${WARN}08` : `${GOOD}08`, border: `1px solid ${totalDue > 0 ? WARN + "18" : GOOD + "18"}` }}>
                <FeesIcon className="h-4 w-4" style={{ color: totalDue > 0 ? WARN : GOOD }} />
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>Fees Due</p>
                  <p className="text-sm font-bold" style={{ color: INK }}>{totalDue > 0 ? fmtUGX(totalDue) : "Clear"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ═════════ HOUSEHOLD WELLBEING ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}>
        <PremiumCard>
          <div className="px-5 sm:px-6 py-4 border-b flex items-center gap-3" style={{ borderColor: "#EDE4D4" }}>
            <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: `${GOOD}12` }}>
              <Heart className="h-5 w-5" style={{ color: GOOD }} />
            </div>
            <div>
              <h3 className="text-base font-semibold tracking-tight" style={{ color: INK }}>Family Status</h3>
              <p className="text-xs" style={{ color: MUTED }}>Everything looks good today.</p>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {wellbeingChecks.map((check, i) => (
                <motion.div
                  key={check.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15 + i * 0.06 }}
                  whileHover={{ y: -3 }}
                  className="flex flex-col items-center gap-2.5 p-4 rounded-[14px] border transition-all"
                  style={{ borderColor: `${check.color}20`, background: `${check.color}05` }}
                >
                  <ProgressRing value={check.pct} size={56} stroke={5} color={check.color} />
                  <div className="text-center">
                    <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: MUTED }}>{check.label}</p>
                    <p className="text-xs font-semibold mt-0.5" style={{ color: INK }}>{check.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </PremiumCard>
      </motion.div>

      {/* ═════════ LIVING CHILD CARDS ═════════ */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold tracking-tight" style={{ color: INK }}>Your Children</h3>
            <p className="text-sm font-medium mt-0.5" style={{ color: MUTED }}>Verified through school invitations</p>
          </div>
        </div>
        <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
          {students.map((s, i) => (
            <LivingChildCard key={s.id} student={s} fees={fees} checkpoints={checkpoints} goChild={goChild} index={i} />
          ))}
        </div>
      </div>

      {/* ═════════ HOUSEHOLD INTELLIGENCE ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
        <PremiumCard accent={BRAND_GRADIENT}>
          <SectionHeader title="Family Insights" subtitle="Intelligent guidance for your family" icon={Sparkles} iconBg={`${INK}12`} />
          <div className="p-5 sm:p-6 grid gap-3 sm:grid-cols-2">
            {insights.map((insight, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 + i * 0.08 }}
                className="group flex items-start gap-3 p-4 rounded-[14px] border transition-all cursor-pointer"
                style={{ background: `${insight.color}06`, borderColor: `${insight.color}15` }}
              >
                <div className="h-9 w-9 rounded-[12px] flex items-center justify-center shrink-0 transition-transform group-hover:scale-110" style={{ background: `${insight.color}15` }}>
                  <insight.icon className="h-4 w-4" style={{ color: insight.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-relaxed" style={{ color: INK_SOFT }}>{insight.text}</p>
                  <button className="text-[10px] font-semibold mt-1 flex items-center gap-0.5 opacity-70 group-hover:opacity-100 transition-opacity" style={{ color: insight.color }}>
                    {insight.action} <ChevronRight className="h-2.5 w-2.5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </PremiumCard>
      </motion.div>

      {/* ═════════ EDUCATION BUDGET ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.5 }}>
        <PremiumCard>
          <SectionHeader title="Education Budget" subtitle={`${fmtUGX(totalPaid)} of ${fmtUGX(totalFees)}`} icon={WalletIcon} iconBg={`${GOOD}12`} />
          <div className="p-5 sm:p-6">
            {/* Hero progress */}
            <div className="flex items-center gap-6 mb-6">
              <ProgressRing value={feeProgress} size={96} stroke={8} color={GOOD} />
              <div className="flex-1 min-w-0">
                <p className="text-3xl font-semibold tracking-tight" style={{ color: INK }}>{Math.round(feeProgress)}%</p>
                <p className="text-sm mt-1" style={{ color: MUTED }}>Paid · {fmtUGX(totalDue)} remaining</p>
                {totalDue > 0 && <p className="text-xs font-semibold mt-2" style={{ color: DANGER }}>Due Monday</p>}
              </div>
              <div className="hidden sm:flex flex-col items-end gap-1">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: `${GOOD}10` }}>
                  <ArrowDownRight className="h-3.5 w-3.5" style={{ color: GOOD }} />
                  <span className="text-xs font-bold" style={{ color: GOOD }}>{fmtUGX(totalPaid)}</span>
                </div>
                {totalDue > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: `${DANGER}10` }}>
                    <ArrowUpRight className="h-3.5 w-3.5" style={{ color: DANGER }} />
                    <span className="text-xs font-bold" style={{ color: DANGER }}>{fmtUGX(totalDue)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Per-child fee breakdown */}
            <div className="space-y-4">
              {students.map((s, i) => {
                const childFees = fees.filter(f => f.student_id === s.id);
                const childTotal = childFees.reduce((a, f) => a + f.total_ugx, 0);
                const childPaid = childFees.reduce((a, f) => a + f.paid_ugx, 0);
                const childDue = Math.max(0, childTotal - childPaid);
                const pct = childTotal > 0 ? (childPaid / childTotal) * 100 : 100;
                const color = childColor(students, s.id);
                return (
                  <motion.div key={s.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.08 }} className="flex items-center gap-4">
                    <Avatar name={s.full_name} color={color} size={40} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <p className="text-sm font-semibold truncate" style={{ color: INK }}>{s.full_name}</p>
                        <p className="text-xs font-semibold shrink-0" style={{ color: childDue > 0 ? DANGER : GOOD }}>{childDue > 0 ? `${fmtUGX(childDue)} due` : "Paid"}</p>
                      </div>
                      <MiniBar value={childPaid} max={childTotal} color={pct >= 100 ? GOOD : pct >= 50 ? WARN : DANGER} />
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Wallet categories — simplified to 4, unified palette */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t" style={{ borderColor: "#EDE4D4" }}>
              {[
                { label: "Wallets", value: fmtUGX(totalWallets), icon: WalletIcon, color: GOOD },
                { label: "Transport", value: "Paid", icon: Bus, color: INK },
                { label: "Meals", value: "Active", icon: Utensils, color: INK },
                { label: "Savings", value: fmtUGX(50000), icon: TrendingUp, color: GOOD },
              ].map((item, i) => (
                <motion.div key={item.label} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35 + i * 0.04 }} whileHover={{ y: -2 }} className="flex items-center gap-2.5 p-3 rounded-[12px] transition-all" style={{ background: `${item.color}06` }}>
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${item.color}12` }}>
                    <item.icon className="h-4 w-4" style={{ color: item.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[9px] font-semibold uppercase tracking-tight" style={{ color: MUTED }}>{item.label}</p>
                    <p className="text-xs font-bold truncate" style={{ color: INK }}>{item.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </PremiumCard>
      </motion.div>

      {/* ═════════ FAMILY MEMORIES ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}>
        <PremiumCard>
          <SectionHeader title="Family Memories" subtitle="Milestones in your children's journey" icon={Award} iconBg={`${INK}12`} />
          <div className="p-5 sm:p-6">
            <div className="relative pl-6">
              <div className="absolute left-2 top-2 bottom-2 w-px" style={{ background: "#E2D6C0" }} />
              {memories.map((mem, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 + i * 0.1 }} whileHover={{ x: 4 }} className="relative flex items-center gap-4 pb-5 last:pb-0 cursor-pointer">
                  <div className="absolute -left-[18px] w-4 h-4 rounded-full flex items-center justify-center transition-transform hover:scale-125" style={{ background: mem.color, boxShadow: "0 0 0 3px white" }}>
                    <mem.icon className="w-2.5 h-2.5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold" style={{ color: mem.color }}>{mem.year}</p>
                    <p className="text-sm font-medium" style={{ color: INK }}>{mem.title}</p>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5" style={{ color: "#A68A5A" }} />
                </motion.div>
              ))}
            </div>
          </div>
        </PremiumCard>
      </motion.div>

      {/* ═════════ FAMILY NETWORK ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.5 }}>
        <PremiumCard>
          <SectionHeader title="Family Network" subtitle="Trusted relationships & guardians" icon={ShieldIcon} iconBg={`${GOOD}12`} />
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { icon: Users, label: "Parents", value: "2", color: INK },
                { icon: UserCheck, label: "Guardians", value: "1", color: GOOD },
                { icon: SchoolIcon, label: "Schools", value: String(schools.size), color: INK },
                { icon: Heart, label: "Emergency", value: "3", color: DANGER },
                { icon: Phone, label: "Pickup Auth.", value: "2", color: INK },
                { icon: Link2, label: "Siblings", value: students.length > 1 ? String(students.length - 1) : "0", color: GOOD },
              ].map((node, i) => (
                <motion.div key={node.label} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 + i * 0.06 }} whileHover={{ y: -3 }} className="flex flex-col items-center gap-2 p-4 rounded-[16px] border transition-all" style={{ borderColor: "#E2D6C0", background: `${node.color}05` }}>
                  <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: `${node.color}12` }}>
                    <node.icon className="h-5 w-5" style={{ color: node.color }} />
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold" style={{ color: INK }}>{node.value}</p>
                    <p className="text-[10px] font-medium" style={{ color: MUTED }}>{node.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </PremiumCard>
      </motion.div>
    </div>
  );
}
